import type CardClient from '@atlaskit/link-provider/client';
import type { CardStore } from '@atlaskit/linking-common/store';

import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';

import { jsonLdToEnhancedSmartLink } from './jsonLdToEnhancedSmartLink.ts';
import type { EnhancedSmartLink } from './smartLinkTypes.ts';
import { toJiraIssueResolutionUrl } from './toJiraIssueResolutionUrl.ts';
import {
	isUnsupportedUrlError,
	isUrlUnresolvable,
	markUrlUnresolvable,
} from './unresolvableUrls.ts';

export type ResolvedSmartLink = {
	link: EnhancedSmartLink;
	/**
	 * Whether metadata was actually obtained. `false` means the link carries only
	 * the url and occurrenceIndex it went in with, so callers can choose between
	 * rendering it bare (outgoing links, where the url came from the page itself)
	 * and dropping it (incoming links, where an unresolvable row is just noise).
	 */
	isResolved: boolean;
};

/**
 * Resolves one URL into an `EnhancedSmartLink`: SmartCard store first (free,
 * synchronous), falling back to `CardClient.fetchData` (network) on a cache miss.
 *
 * ORS failures are never thrown — one broken link must not prevent the rest from
 * resolving. A URL with no ORS provider is remembered (`markUrlUnresolvable`) so
 * later passes stop re-requesting it, and is not reported: a plain hyperlink is
 * expected to be unsupported. Anything else is reported to monitoring.
 */
export async function resolveSmartLinkByUrl(
	url: string,
	occurrenceIndex: number,
	store: { getState: () => CardStore },
	cardClient: CardClient,
): Promise<ResolvedSmartLink> {
	const resolutionUrl = toJiraIssueResolutionUrl(url);
	const cached = store.getState()[resolutionUrl]?.details;
	let response = cached?.data ? cached : null;

	// Failed resolves never reach the store, so every pass would re-request them.
	if (!response && !isUrlUnresolvable(resolutionUrl)) {
		response = await cardClient.fetchData(resolutionUrl).catch((err: unknown) => {
			// Expected for a plain hyperlink, so remember it instead of reporting it.
			if (isUnsupportedUrlError(err)) {
				markUrlUnresolvable(resolutionUrl);
				return null;
			}
			const errorObj = err instanceof Error ? err : new Error(String(err));
			getMonitoringClient().submitError(errorObj, {
				attribution: Attribution.INTEGRATIONS,
			});
			return null;
		});
	}

	return {
		link: jsonLdToEnhancedSmartLink(url, occurrenceIndex, response),
		isResolved: response != null,
	};
}
